import type { OrgRef } from '@ni/shared';

/**
 * Which org the app runs under, and how that survives a reload.
 *
 * The stored id is a preference, never an authority: membership can end while
 * the tab is open, and the server 404s an org the caller no longer belongs to
 * rather than 403ing it (server/src/auth/org-access.guard.ts), so a removal is
 * invisible in responses. `me.orgs` is the only place it shows, which is why
 * the preference is validated against that list on every `/me` read rather than
 * trusted once at bootstrap.
 *
 * Storage follows `auth/dev-users.ts`: every access wrapped, because a browser
 * with site data blocked throws on the getter and losing the preference is a
 * far better answer than a blank screen.
 */
export const ORG_STORAGE_KEY = 'ni.org';

export function readStoredOrg(): string | null {
  try {
    return (
      new URLSearchParams(window.location.search).get('org') ??
      window.localStorage.getItem(ORG_STORAGE_KEY)
    );
  } catch {
    return null;
  }
}

export function rememberOrg(id: string): void {
  try {
    window.localStorage.setItem(ORG_STORAGE_KEY, id);
  } catch {
    // Remembering is a convenience, never a requirement.
  }
}

export function forgetOrg(): void {
  try {
    window.localStorage.removeItem(ORG_STORAGE_KEY);
  } catch {
    // As above.
  }
}

/**
 * Pure. Picks the org to run under.
 *
 * `stale` is true when `preferredId` was set but is no longer in `orgs` — the
 * caller should forget it, because the only ways back to a valid preference are
 * the reader picking again or membership being restored.
 */
export function chooseOrg<T extends OrgRef>(
  orgs: T[],
  preferredId: string | null,
): { org: T | null; stale: boolean } {
  if (preferredId) {
    const preferred = orgs.find((org) => org.id === preferredId);
    if (preferred) return { org: preferred, stale: false };
    // `orgs` is name-ASC from the server, so the fallback is stable rather than
    // whichever row the last query happened to return first.
    return { org: orgs[0] ?? null, stale: true };
  }
  return { org: orgs[0] ?? null, stale: false };
}
